// CMS Fraud Rules Management
import React, { useState, useEffect } from 'react';
import { useCMS } from './CMSContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Shield, Plus, Trash2, Edit, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RULE_TYPES = [
  { value: 'multiple_bookings', label: 'Multiple Bookings' },
  { value: 'high_value_booking', label: 'High Value Booking' },
  { value: 'suspicious_country', label: 'Suspicious Country' },
  { value: 'rapid_pass_usage', label: 'Rapid Pass Usage' },
  { value: 'multiple_refunds', label: 'Multiple Refunds' },
  { value: 'invalid_pass_attempts', label: 'Invalid Pass Attempts' },
  { value: 'account_sharing', label: 'Account Sharing' },
  { value: 'nationality_gate', label: 'Nationality Gate (TC Kimlik)' }
];

const SEVERITIES = [
  { value: 'low', label: 'Low', color: 'bg-blue-500' },
  { value: 'medium', label: 'Medium', color: 'bg-yellow-500' },
  { value: 'high', label: 'High', color: 'bg-orange-500' },
  { value: 'critical', label: 'Critical', color: 'bg-red-500' }
];

const CMSFraudRules = () => {
  const { token } = useCMS();
  const [rules, setRules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    rule_type: 'rapid_pass_usage',
    description: '',
    severity: 'medium',
    threshold: 3,
    time_window_hours: 24,
    auto_suspend: false,
    notify_admin: true,
    is_active: true,
    config: {
      trigger_country: 'TR',
      blocked_destinations: 'TR',
      exempt_flag: 'has_tc_kimlik_card'
    }
  });

  useEffect(() => {
    fetchRules();
  }, []);

  const fetchRules = async () => {
    try {
      const res = await fetch(`${API}/cms/fraud-rules`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      setRules(data.rules || []);
    } catch (error) {
      toast.error('Failed to load fraud rules');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = editingRule 
        ? `${API}/cms/fraud-rules/${editingRule.rule_id}`
        : `${API}/cms/fraud-rules`;
      
      // Build clean payload — include config only for nationality_gate rules
      const payload = { ...formData };
      if (payload.rule_type === 'nationality_gate') {
        payload.config = {
          ...(editingRule?.config || {}),
          trigger_country: (formData.config?.trigger_country || 'TR').toUpperCase(),
          blocked_destinations: (formData.config?.blocked_destinations || 'TR')
            .split(',').map(s => s.trim().toUpperCase()).filter(Boolean),
          exempt_flag: formData.config?.exempt_flag || 'has_tc_kimlik_card',
        };
      } else {
        delete payload.config;
      }
      
      const res = await fetch(url, {
        method: editingRule ? 'PATCH' : 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        toast.success(editingRule ? 'Rule updated' : 'Rule created');
        setIsDialogOpen(false);
        setEditingRule(null);
        resetForm();
        fetchRules();
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to save rule');
      }
    } catch (error) {
      toast.error('Failed to save rule');
    }
  };

  const handleDelete = async (ruleId) => {
    if (!window.confirm('Are you sure you want to delete this rule?')) return;
    
    try {
      const res = await fetch(`${API}/cms/fraud-rules/${ruleId}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (res.ok) {
        toast.success('Rule deleted');
        fetchRules();
      } else {
        toast.error('Failed to delete rule');
      }
    } catch (error) {
      toast.error('Failed to delete rule');
    }
  };

  const handleToggleActive = async (rule) => {
    try {
      const res = await fetch(`${API}/cms/fraud-rules/${rule.rule_id}`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ is_active: !rule.is_active })
      });

      if (res.ok) {
        toast.success(`Rule ${!rule.is_active ? 'enabled' : 'disabled'}`);
        fetchRules();
      }
    } catch (error) {
      toast.error('Failed to toggle rule');
    }
  };

  const openEditDialog = (rule) => {
    setEditingRule(rule);
    setFormData({
      name: rule.name,
      rule_type: rule.rule_type,
      description: rule.description || '',
      severity: rule.severity,
      threshold: rule.threshold,
      time_window_hours: rule.time_window_hours,
      auto_suspend: rule.auto_suspend,
      notify_admin: rule.notify_admin,
      is_active: rule.is_active,
      config: {
        trigger_country: rule.config?.trigger_country || 'TR',
        blocked_destinations: Array.isArray(rule.config?.blocked_destinations)
          ? rule.config.blocked_destinations.join(', ')
          : (rule.config?.blocked_destinations || 'TR'),
        exempt_flag: rule.config?.exempt_flag || 'has_tc_kimlik_card'
      }
    });
    setIsDialogOpen(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      rule_type: 'rapid_pass_usage',
      description: '',
      severity: 'medium',
      threshold: 3,
      time_window_hours: 24,
      auto_suspend: false,
      notify_admin: true,
      is_active: true,
      config: {
        trigger_country: 'TR',
        blocked_destinations: 'TR',
        exempt_flag: 'has_tc_kimlik_card'
      }
    });
  };

  const getSeverityBadge = (severity) => {
    const sev = SEVERITIES.find(s => s.value === severity);
    return <Badge className={`${sev?.color || 'bg-gray-500'} text-white`}>{severity}</Badge>;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="cms-fraud-rules">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-2">
            <Shield className="h-6 w-6" /> Fraud Detection Rules
          </h1>
          <p className="text-slate-400">Configure automated fraud detection and prevention</p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) { setEditingRule(null); resetForm(); } }}>
          <DialogTrigger asChild>
            <Button data-testid="create-rule-btn">
              <Plus className="h-4 w-4 mr-2" /> Create Rule
            </Button>
          </DialogTrigger>
          <DialogContent className="bg-white border-gray-200 text-gray-900 max-w-lg">
            <DialogHeader>
              <DialogTitle className="text-gray-900">{editingRule ? 'Edit Rule' : 'Create Fraud Rule'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-gray-700">Rule Name</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900"
                  required
                />
              </div>
              <div>
                <Label className="text-gray-700">Rule Type</Label>
                <Select value={formData.rule_type} onValueChange={(v) => setFormData({ ...formData, rule_type: v })}>
                  <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {RULE_TYPES.map(t => (
                      <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-gray-700">Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="bg-white border-gray-300 text-gray-900"
                  rows={2}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-700">Severity</Label>
                  <Select value={formData.severity} onValueChange={(v) => setFormData({ ...formData, severity: v })}>
                    <SelectTrigger className="bg-white border-gray-300 text-gray-900">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {SEVERITIES.map(s => (
                        <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-gray-700">Threshold</Label>
                  <Input
                    type="number"
                    min="1"
                    value={formData.threshold}
                    onChange={(e) => setFormData({ ...formData, threshold: parseInt(e.target.value) })}
                    className="bg-white border-gray-300 text-gray-900"
                  />
                </div>
              </div>
              <div>
                <Label className="text-gray-700">Time Window (hours)</Label>
                <Input
                  type="number"
                  min="1"
                  value={formData.time_window_hours}
                  onChange={(e) => setFormData({ ...formData, time_window_hours: parseInt(e.target.value) })}
                  className="bg-white border-gray-300 text-gray-900"
                />
              </div>
              
              {/* Nationality Gate specific config */}
              {formData.rule_type === 'nationality_gate' && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg space-y-3" data-testid="nationality-gate-config">
                  <p className="text-sm font-semibold text-amber-900">Nationality Gate Configuration</p>
                  <div>
                    <Label className="text-gray-700">Trigger Country (ISO-2)</Label>
                    <Input
                      value={formData.config?.trigger_country || ''}
                      onChange={(e) => setFormData({ ...formData, config: { ...formData.config, trigger_country: e.target.value.toUpperCase() }})}
                      placeholder="TR"
                      maxLength={2}
                      className="bg-white border-gray-300 text-gray-900 uppercase"
                      data-testid="gate-trigger-country"
                    />
                    <p className="text-xs text-gray-500 mt-1">Which visitor country triggers the popup (e.g. TR = Turkey)</p>
                  </div>
                  <div>
                    <Label className="text-gray-700">Blocked Destinations (ISO-2, comma-separated)</Label>
                    <Input
                      value={formData.config?.blocked_destinations || ''}
                      onChange={(e) => setFormData({ ...formData, config: { ...formData.config, blocked_destinations: e.target.value }})}
                      placeholder="TR"
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="gate-blocked-destinations"
                    />
                    <p className="text-xs text-gray-500 mt-1">Hotel countries that are hidden when visitor declines (e.g. TR)</p>
                  </div>
                  <div>
                    <Label className="text-gray-700">Exempt Flag</Label>
                    <Input
                      value={formData.config?.exempt_flag || ''}
                      onChange={(e) => setFormData({ ...formData, config: { ...formData.config, exempt_flag: e.target.value }})}
                      placeholder="has_tc_kimlik_card"
                      className="bg-white border-gray-300 text-gray-900"
                      data-testid="gate-exempt-flag"
                    />
                    <p className="text-xs text-gray-500 mt-1">Internal flag identifier (keep as default unless you know what you're doing)</p>
                  </div>
                  <p className="text-xs text-amber-800">
                    💡 Popup translations are stored in the rule's config.popup_text_i18n and seeded automatically for 11 languages.
                  </p>
                </div>
              )}
              
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.auto_suspend}
                    onCheckedChange={(v) => setFormData({ ...formData, auto_suspend: v })}
                  />
                  <Label className="text-gray-700">Auto-suspend on trigger</Label>
                </div>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={formData.notify_admin}
                    onCheckedChange={(v) => setFormData({ ...formData, notify_admin: v })}
                  />
                  <Label className="text-gray-700">Notify admin</Label>
                </div>
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Cancel</Button>
                <Button type="submit">{editingRule ? 'Update' : 'Create'} Rule</Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {rules.length === 0 ? (
        <Card className="bg-slate-800 border-slate-700">
          <CardContent className="py-12 text-center">
            <AlertTriangle className="h-12 w-12 text-slate-500 mx-auto mb-4" />
            <p className="text-slate-400">No fraud rules configured yet</p>
            <p className="text-sm text-slate-500">Create your first rule to start detecting suspicious activity</p>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {rules.map(rule => (
            <Card key={rule.rule_id} className={`bg-slate-800 border-slate-700 ${!rule.is_active ? 'opacity-60' : ''}`}>
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <Switch
                      checked={rule.is_active}
                      onCheckedChange={() => handleToggleActive(rule)}
                    />
                    <div>
                      <h3 className="font-semibold text-white">{rule.name}</h3>
                      <p className="text-sm text-slate-400">{rule.description || RULE_TYPES.find(t => t.value === rule.rule_type)?.label}</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-4">
                    {getSeverityBadge(rule.severity)}
                    <div className="text-sm text-slate-400">
                      <span className="font-medium">{rule.threshold}</span> in <span className="font-medium">{rule.time_window_hours}h</span>
                    </div>
                    <div className="text-sm text-slate-400">
                      Triggers: <span className="font-medium text-white">{rule.triggers_count || 0}</span>
                    </div>
                    <div className="flex gap-2">
                      <Button size="sm" variant="ghost" onClick={() => openEditDialog(rule)}>
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button size="sm" variant="ghost" className="text-red-400 hover:text-red-300" onClick={() => handleDelete(rule.rule_id)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
                {(rule.auto_suspend || rule.notify_admin) && (
                  <div className="mt-2 flex gap-2">
                    {rule.auto_suspend && <Badge variant="outline" className="text-red-400 border-red-400">Auto-suspend</Badge>}
                    {rule.notify_admin && <Badge variant="outline" className="text-blue-400 border-blue-400">Notify admin</Badge>}
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default CMSFraudRules;
